import { jwtDecode } from 'jwt-decode';
import { storageService, STORAGE_KEYS } from './storage.service';
import type { AuthUser, CollegeProfile, CoordinatorProfile } from '@/types/auth.types';

// Re-export types for convenience
export type { AuthUser, CollegeProfile, CoordinatorProfile };

// JWT payload structure from Flutter app (auth_service.dart lines 168-175)
interface JWTPayload {
  UserUId: string;
  email: string;
  Role: string;
  OrganizationUId: string;
  OrganizationName: string;
  Name: string;
  exp: number;
  iat: number;
}

class AuthService {
  // Get access token
  getAccessToken(): string | null {
    return storageService.getString(STORAGE_KEYS.ACCESS_TOKEN);
  }

  // Get refresh token
  getRefreshToken(): string | null {
    return storageService.getString(STORAGE_KEYS.REFRESH_TOKEN);
  }

  // Save login data
  async saveLoginData(accessToken: string, refreshToken: string): Promise<void> {
    storageService.setString(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    storageService.setString(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);

    // Decode token and save user info (using Flutter field names)
    try {
      const decoded = jwtDecode<JWTPayload>(accessToken);
      storageService.setString(STORAGE_KEYS.USER_UID, decoded.UserUId);
      storageService.setString(STORAGE_KEYS.ORGANIZATION_UID, decoded.OrganizationUId);
      storageService.setString(STORAGE_KEYS.USER_ROLE, decoded.Role);
      // Store additional fields
      storageService.setString(STORAGE_KEYS.USER_NAME, decoded.Name);
      storageService.setString(STORAGE_KEYS.ORGANIZATION_NAME, decoded.OrganizationName);
      storageService.setString(STORAGE_KEYS.USER_EMAIL, decoded.email);
     } catch (error) {
      console.error('Error decoding token:', error);
    }
  }

  // Get current user from stored token
  async getCurrentUser(): Promise<AuthUser | null> {
    const token = this.getAccessToken();
    if (!token) return null;

    try {
      const decoded = jwtDecode<JWTPayload>(token);
      return {
        uid: decoded.UserUId,
        email: decoded.email,
        role: decoded.Role,
        organizationUid: decoded.OrganizationUId,
      };
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  // Validate token
  async validateToken(): Promise<boolean> {
    const token = this.getAccessToken();
    if (!token) return false;

    try {
      const decoded = jwtDecode<JWTPayload>(token);
      const currentTime = Date.now() / 1000;

      // Check if token is expired (with 60 second buffer)
      if (decoded.exp < currentTime - 60) {
        return false;
      }

      return true;
    } catch (error) {
      console.error('Token validation error:', error);
      return false;
    }
  }

  // Check if token needs refresh
  isTokenExpiringSoon(): boolean {
    const token = this.getAccessToken();
    if (!token) return true;

    try {
      const decoded = jwtDecode<JWTPayload>(token);
      const currentTime = Date.now() / 1000;
      const timeUntilExpiry = decoded.exp - currentTime;

      // Refresh if less than 5 minutes until expiry
      return timeUntilExpiry < 300;
    } catch {
      return true;
    }
  }

  // Clear auth data
  async clearAuthData(): Promise<void> {
    storageService.clearAuthData();
  }

  // Get stored user info
  getUserUid(): string | null {
    return storageService.getString(STORAGE_KEYS.USER_UID);
  }

  getOrganizationUid(): string | null {
    return storageService.getString(STORAGE_KEYS.ORGANIZATION_UID);
  }

  getOrganizationName(): string | null {
    return storageService.getString(STORAGE_KEYS.ORGANIZATION_NAME);
  }

  getUserRole(): string | null {
    return storageService.getString(STORAGE_KEYS.USER_ROLE);
  }

  getUserName(): string | null {
    return storageService.getString(STORAGE_KEYS.USER_NAME);
  }

  getUserEmail(): string | null {
    return storageService.getString(STORAGE_KEYS.USER_EMAIL);
  }

  getStatus(): string | null {
    return storageService.getString(STORAGE_KEYS.STATUS);
  }

  setStatus(status: string): void {
    storageService.setString(STORAGE_KEYS.STATUS, status);
  }

  // Check if user is college admin
  isCollegeAdmin(): boolean {
    return this.getUserRole() === 'college-admin';
  }

  // User permissions management
  setUserPermissions(permissions: UserPermissionsData): void {
    storageService.setJSON(STORAGE_KEYS.USER_PERMISSIONS, permissions);
  }

  getUserPermissions(): UserPermissionsData | null {
    return storageService.getJSON<UserPermissionsData>(STORAGE_KEYS.USER_PERMISSIONS);
  }

  // Check if user has a specific permission
  hasPermission(permissionName: string): boolean {
    // College admins have all permissions
    if (this.isCollegeAdmin()) {
      return true;
    }

    const permissions = this.getUserPermissions();
    if (!permissions?.permissions) {
      return true; // Default to true if no permissions data
    }

    const permission = permissions.permissions.find((p) => p.name === permissionName);
    return permission?.value ?? true; // Default to true if permission not found
  }
}

// User permissions data structure
export interface UserPermissionsData {
  uId?: string;
  userUId?: string;
  organizationUId?: string;
  roleName?: string;
  type?: string;
  permissions?: Array<{
    name?: string;
    displayName?: string;
    value?: boolean;
  }>;
}

// Export singleton instance
export const authService = new AuthService();
